import { AICoachGoalItem } from 'api/LoanOfferApi';
import { AICoachGoalType } from 'enums/AICoach';
import { DebtSummary, HardOfferData, HardOfferSummary } from 'handlers/applicationData';
import {
  getGoalTargetAmount,
  getPersonalDebtAmount,
  getTimeToTargetFromGoal,
} from 'components/FinancialCheckup/FinancialDashboard/YourGoalSection/getYourGoalTrajectory';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';

export enum AcceleratePrimaryAction {
  SeeMyPlan = 'seeMyPlan',
  EnrollScoreIQ = 'enrollScoreIQ',
}

export enum MonthsAdverb {
  Sooner = 'sooner',
  Faster = 'faster',
}

export interface TrajectoryComparisonValues {
  currentValue: string;
  planneryValue: string;
}

export interface AccelerateYourGoalsCardContent {
  title: string;
  subtitle?: string;
  bullets?: string[];
  primaryButtonText: string;
  primaryAction: AcceleratePrimaryAction;
  showScoreIQAttribution?: boolean;
}

const SEE_MY_PLAN = 'SEE MY PLAN';
const ENROLL_IN_SCORE_IQ = 'ENROLL IN SCOREIQ';
const SCORE_IQ_TITLE = 'Plannery ScoreIQ©';
export const MIN_PLANNERY_DEBT_AMOUNT = 1000;

const formatMonths = (months: number): string => {
  const rounded = Math.max(1, Math.round(months));
  return `${rounded} ${rounded === 1 ? 'month' : 'months'}`;
};

const getMonthsSooner = (
  goal?: AICoachGoalItem,
  monthlyPaymentSaved?: number,
  debtSummary?: DebtSummary,
): number | undefined => {
  const { monthlySavingsAmount } = goal ?? {};
  const targetValue = getGoalTargetAmount(goal, debtSummary);

  if (
    !targetValue ||
    !monthlySavingsAmount ||
    monthlySavingsAmount <= 0 ||
    !monthlyPaymentSaved ||
    monthlyPaymentSaved <= 0
  ) {
    return undefined;
  }

  const monthsSooner =
    Math.ceil(targetValue / monthlySavingsAmount) -
    Math.ceil(targetValue / (monthlySavingsAmount + monthlyPaymentSaved));

  return monthsSooner > 0 ? monthsSooner : undefined;
};

const hasPositiveOfferSavings = (hardOffer: Partial<HardOfferData>): boolean => {
  const { moneySaved, monthsSaved, monthlyPaymentSaved } = hardOffer.offerSummary ?? {};
  return (moneySaved ?? 0) > 0 || (monthsSaved ?? 0) > 0 || (monthlyPaymentSaved ?? 0) > 0;
};

const withMonths = (base: string, months: number | undefined, adverb: MonthsAdverb): string =>
  months && months > 0 ? `${base} ${months} months ${adverb}` : base;

const offerBullets = (monthlyPaymentSaved: number | undefined, thirdBullet: string): string[] => {
  const bullets = ['Consolidate debt with Plannery'];

  if (monthlyPaymentSaved && monthlyPaymentSaved > 0) {
    bullets.push(`Save ${formatMonetaryAmount(monthlyPaymentSaved)}/mo on debt payments`);
  }

  bullets.push(thirdBullet);
  return bullets;
};

const resolvePayOffTrajectory = (
  hardOffer: Partial<HardOfferData>,
  currentMonths?: number,
  planneryMonths?: number,
): TrajectoryComparisonValues | null => {
  const monthsSaved = hardOffer.offerSummary?.monthsSaved;
  if (!monthsSaved || monthsSaved <= 0 || !currentMonths || !planneryMonths) return null;

  return {
    currentValue: formatMonths(currentMonths),
    planneryValue: formatMonths(planneryMonths),
  };
};

export const resolveTrajectoryComparison = (
  hardOffer: Partial<HardOfferData>,
  currentMonths?: number,
  planneryMonths?: number,
  debtSummary?: DebtSummary,
  goal?: AICoachGoalItem,
): TrajectoryComparisonValues | null => {
  if (goal?.type === AICoachGoalType.ImproveCreditScore) return null;

  if (goal?.type === AICoachGoalType.PayDownCreditCardDebt) {
    return resolvePayOffTrajectory(hardOffer, currentMonths, planneryMonths);
  }

  const timeToTarget = getTimeToTargetFromGoal(goal, debtSummary);
  const monthsSooner = getMonthsSooner(goal, hardOffer.offerSummary?.monthlyPaymentSaved, debtSummary);

  if (!monthsSooner || monthsSooner <= 0) return null;

  return {
    currentValue: formatMonths(timeToTarget),
    planneryValue: formatMonths(timeToTarget - monthsSooner),
  };
};

const resolvePayOffDebtTitle = (monthsSaved: number | undefined): string | null => {
  if (!monthsSaved || monthsSaved <= 0) return null;

  return withMonths('Pay off debt', monthsSaved, MonthsAdverb.Sooner);
};

const resolveOfferContent = (
  goal: AICoachGoalItem,
  hardOffer: Partial<HardOfferData>,
  debtSummary?: DebtSummary,
): AccelerateYourGoalsCardContent | null => {
  const { offerSummary } = hardOffer;
  const monthsSaved = offerSummary?.monthsSaved;
  const monthlyPaymentSaved = offerSummary?.monthlyPaymentSaved;
  const expenseName = goal.description?.toLowerCase() ?? 'your expense';
  const monthsSooner = getMonthsSooner(goal, monthlyPaymentSaved, debtSummary);

  const usesMonthsSooner =
    goal.type !== AICoachGoalType.PayDownCreditCardDebt && goal.type !== AICoachGoalType.ImproveCreditScore;

  if (usesMonthsSooner && !monthsSooner) return null;

  const baseContent = {
    primaryButtonText: SEE_MY_PLAN,
    primaryAction: AcceleratePrimaryAction.SeeMyPlan,
  };

  switch (goal.type) {
    case AICoachGoalType.PayDownCreditCardDebt: {
      const title = resolvePayOffDebtTitle(monthsSaved);
      if (!title) return null;

      return {
        ...baseContent,
        title,
      };
    }
    case AICoachGoalType.ImproveCreditScore:
      return {
        ...baseContent,
        title: 'Pay off debt to improve your credit score',
      };
    case AICoachGoalType.BuildEmergencyFund:
      return {
        ...baseContent,
        title: withMonths('Save for emergency', monthsSooner, MonthsAdverb.Faster),
        bullets: offerBullets(monthlyPaymentSaved, 'Put this to your emergency fund'),
      };
    case AICoachGoalType.PayDownStudentLoanDebt:
      return {
        ...baseContent,
        title: withMonths('Pay down student debt', monthsSooner, MonthsAdverb.Faster),
        bullets: offerBullets(monthlyPaymentSaved, 'Put this to pay student debt faster'),
      };
    case AICoachGoalType.SaveForRetirement:
      return {
        ...baseContent,
        title: withMonths('Save for retirement', monthsSooner, MonthsAdverb.Sooner),
        bullets: offerBullets(monthlyPaymentSaved, 'Put this to save for retirement'),
      };
    case AICoachGoalType.SaveForSpecificExpense:
    case AICoachGoalType.Other:
    default:
      return {
        ...baseContent,
        title: withMonths(`Save for ${expenseName}`, monthsSooner, MonthsAdverb.Sooner),
        bullets: offerBullets(monthlyPaymentSaved, `Put this to save for ${expenseName}`),
      };
  }
};

const resolveRejectedContent = (
  offerSummary?: HardOfferSummary,
  scoreImprovement?: number,
  goal?: AICoachGoalItem,
): AccelerateYourGoalsCardContent | null => {
  if (goal?.type === AICoachGoalType.ImproveCreditScore) return null;

  const baseContent = {
    title: SCORE_IQ_TITLE,
    showScoreIQAttribution: true,
    primaryButtonText: ENROLL_IN_SCORE_IQ,
    primaryAction: AcceleratePrimaryAction.EnrollScoreIQ,
  };

  if (goal?.type === AICoachGoalType.PayDownCreditCardDebt && scoreImprovement) {
    return {
      ...baseContent,
      subtitle: `Boost your credit score by ${scoreImprovement} points and you could qualify to pay off existing debt faster with Plannery`,
    };
  }

  const monthlyPaymentSaved = offerSummary?.monthlyPaymentSaved;
  const bullets = ['Enroll in ScoreIQ to improve credit', 'Consolidate debt with Plannery'];

  if (monthlyPaymentSaved && monthlyPaymentSaved > 0) {
    bullets.push(`Save extra ${formatMonetaryAmount(monthlyPaymentSaved)} towards your goal`);
  }

  return {
    ...baseContent,
    bullets,
  };
};

export const resolveAccelerateYourGoalsCard = (
  isRejected: boolean,
  hardOffer: Partial<HardOfferData>,
  scoreImprovement?: number,
  debtSummary?: DebtSummary,
  goal?: AICoachGoalItem,
): AccelerateYourGoalsCardContent | null => {
  if (getPersonalDebtAmount(debtSummary) < MIN_PLANNERY_DEBT_AMOUNT) return null;

  if (goal?.type === AICoachGoalType.ImproveCreditScore) {
    if (isRejected || !hasPositiveOfferSavings(hardOffer)) return null;

    return resolveOfferContent({ ...goal, type: AICoachGoalType.PayDownCreditCardDebt }, hardOffer, debtSummary);
  }

  const scoreIQContent = resolveRejectedContent(hardOffer.offerSummary, scoreImprovement, goal);

  if (isRejected || !hasPositiveOfferSavings(hardOffer) || !goal) return scoreIQContent;

  return resolveOfferContent(goal, hardOffer, debtSummary) ?? scoreIQContent;
};
